import * as React from 'react';

import { observer } from 'mobx-react';

import { Field, FormInput, FormStore } from '@estee/elc-form';
import { IFormValues } from '@estee/elc-form/typings/views/Form';

import * as classNames from 'classnames';
import { AsYouType } from 'libphonenumber-js/core';

import {
    DEFAULT_MOBILE_DISPLAY_FORMAT,
    DEFAULT_MOBILE_VALUE_FORMAT,
    INPUT_FIELD_HIGHLIGHTED_CLASS
} from '~constants/Constants';
import { IPhoneNumberInputConfig } from '~types/config';
import { fieldClasses } from '~utils/Classes';
import { FieldValidations } from '~utils/FieldValidations';
import { useRemoveHighlightOnFocus } from '~utils/hooks/field';
import {
    createAsYouType,
    ensureCountryCallingCodePrefix,
    formatValue,
    getCountryCodeByCallingCode,
    getDisplayFieldName,
    isCountryCallingCode
} from '~utils/phone-number-helper';

export const TEST_ID_PHONE_INPUT = 'ramp-phone-input';

export interface IPhoneInputProps extends IPhoneNumberInputConfig {
    inputLabel: string;
    name: string;
    fieldValidations: FieldValidations;
    formStore: FormStore<IFormValues>;
    disabled?: boolean;
    initialValue?: string;
    readonly?: boolean;
    highlighted?: boolean;
}

const PhoneInput = observer(function _PhoneInput(props: IPhoneInputProps) {
    const {
        inputLabel,
        name,
        disabled,
        initialValue = '',
        formStore,
        fieldValidations,
        displayFormat = DEFAULT_MOBILE_DISPLAY_FORMAT,
        valueFormat = DEFAULT_MOBILE_VALUE_FORMAT,
        countryCodes = [],
        highlighted = false,
        readonly
    } = props;
    const { isRequired, maxLength, validations } = fieldValidations || {};

    const className = fieldClasses(name, 'phone-input');

    // Create a field to store the display text value of the mobile number field.
    // The actual mobile number value will be stored in form in E.164 format (e.g. +886912345678).
    const displayFieldName = getDisplayFieldName(name);
    const showCountryCallingCode = displayFormat === 'INTERNATIONAL' && countryCodes.length === 1;

    React.useEffect(() => {
        if (countryCodes.length && !formStore.values.countryCode) {
            formStore.setFieldValue('countryCode', countryCodes[0]);
        }
    }, [formStore, countryCodes]);

    const asYouTypeRef = React.useRef<AsYouType>(createAsYouType());
    const handleDisplayFieldChangeRef = React.useRef<(value: string) => void>();

    React.useEffect(() => {
        const { countryCode: formCountryCodeValue } = formStore.values;
        const countryCode = isCountryCallingCode(formCountryCodeValue)
            ? getCountryCodeByCallingCode(formCountryCodeValue)
            : formCountryCodeValue;
        asYouTypeRef.current = createAsYouType(countryCode);
        handleDisplayFieldChangeRef.current = (value: string) => {
            // No initial country is set, e.g., Hong Kong form without `countryCode` selected
            if (!countryCode) {
                return;
            }
            const asYouType = asYouTypeRef.current;
            asYouType.reset();
            const inputValue = asYouType.input(value);
            const phoneNumber = asYouType.getNumber();
            formStore.setFieldValue(name, phoneNumber ? formatValue(phoneNumber, valueFormat) : '');

            if (showCountryCallingCode) {
                asYouType.reset();
                // We need to show 'fixed' prefix, but the value is missing that part
                const withPrefix = asYouType.input(
                    ensureCountryCallingCodePrefix(value, countryCode)
                );
                formStore.setFieldValue(displayFieldName, withPrefix);
            } else {
                const displayValue = phoneNumber?.isValid()
                    ? phoneNumber.format(displayFormat)
                    : inputValue;
                formStore.setFieldValue(displayFieldName, displayValue);
            }
        };
    }, [formStore.values.countryCode]);

    React.useEffect(() => {
        const displayFieldValue = formStore.values[displayFieldName] || initialValue;
        handleDisplayFieldChangeRef.current?.(displayFieldValue);
    }, [handleDisplayFieldChangeRef.current]);

    const onFocus = useRemoveHighlightOnFocus(name);

    return (
        <Field
            name={displayFieldName}
            inputLabel={inputLabel}
            customClassName={classNames(className, {
                [`${INPUT_FIELD_HIGHLIGHTED_CLASS}`]: highlighted
            })}
            isRequired={isRequired}
            disabled={disabled}
            maxLength={maxLength}
            dataTestId={TEST_ID_PHONE_INPUT}
            component={FormInput}
            afterChange={(_displayFieldName: string, value: string) => {
                handleDisplayFieldChangeRef.current?.(value);
            }}
            onFocus={onFocus}
            validations={validations}
            readOnly={readonly}
            inputType="tel"
        />
    );
});

export default PhoneInput;
export { PhoneInput };
